import { useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { Breadcrumb } from '@/components/Breadcrumb';
import { useRouter } from '@/hooks/useRouter';
import type { StoreHit } from '@/integrations/algolia/types';
import {
  countryMessages,
  useCountryOptionsSearch,
} from '@/lib/boutique/helpers';
import { BASE_STORE_FINDER_URL } from '@/lib/const';
import { getTranslatedCountryName } from '@/lib/i18n';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { messages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';

import heroImage from '@/assets/contact-1920.png';

import * as S from './Boutiques.styles';
type OptionType = { label: string; value: string };

export const TopBanner = () => {
  const { locale: useRouterlocale, push, query } = useRouter();
  const languageCode = getRegionFromLocale(useRouterlocale).locale.locale;
  const countryOptions = useCountryOptionsSearch();

  const { formatMessage } = useIntl();
  const { hits } = useHits<StoreHit>();
  const [option, setOption] = useState<OptionType>();
  useEffect(() => {
    if (countryOptions?.length && query) {
      const defaultOption = countryOptions.find(
        (country) => country.value.toLowerCase() === query?.country,
      );
      setOption(defaultOption);
    }
  }, [countryOptions, query]);

  const handleSelectCountry = (opt: OptionType): void => {
    if (opt) {
      const countrySlug = opt.value.toLowerCase();
      push({
        pathname: `/stores/breitling-boutiques/${countrySlug}`,
      });
    }
  };
  const countrySlugUpperCase = Array.isArray(query?.country)
    ? query?.country[0]?.toUpperCase()
    : (query?.country as string)?.toUpperCase();
  const translatedCountryName = () => {
    return getTranslatedCountryName(languageCode, countrySlugUpperCase) ?? '';
  };
  const toStoresString = `${BASE_STORE_FINDER_URL}index.html?country=${countrySlugUpperCase}`;
  const getBtqlistHeadingMessage = (countrySlugUpperCase: string) => {
    const code = countrySlugUpperCase.toLowerCase();
    return (
      countryMessages[code as keyof typeof countryMessages] ??
      messages.usBtqlistHeading
    );
  };
  const bannerImage = hits?.[0]?.photo_gallery?.[0]?.image?.url ?? heroImage;

  return (
    <>
      <S.TopBannerWrapper data-test-id='BoutiquesTopBannerWrapper'>
        <S.BreadCrumbSection data-test-id='BoutiquesBreadCrumbSection'>
          <S.BreadCrumbContent data-test-id='BoutiquesBreadCrumbContent'>
            <Breadcrumb.Wrapper>
              <Breadcrumb
                inverted
                items={[
                  {
                    label: formatMessage(commonMessages.home),
                    target: paths.url(),
                  },
                  {
                    label: formatMessage(messages.boutiquesAndRetailers),
                    target: toStoresString,
                  },
                  {
                    label: translatedCountryName(),
                    target: toStoresString,
                  },
                  {
                    label: formatMessage(
                      getBtqlistHeadingMessage(
                        countrySlugUpperCase.toLowerCase(),
                      ),
                    ),
                    target: paths.asPath(),
                  },
                ]}
              />
            </Breadcrumb.Wrapper>
          </S.BreadCrumbContent>
        </S.BreadCrumbSection>
        <S.StyledContainer maxWidth='lg'>
          <S.TextWrapper data-test-id='BoutiquesTextWrapper'>
            <S.TextContainer data-test-id='BoutiquesTextContainer'>
              <S.StyledHeading data-test-id='BoutiquesStyledHeading'>
                {formatMessage(
                  getBtqlistHeadingMessage(countrySlugUpperCase.toLowerCase()),
                )}
              </S.StyledHeading>
              <div>
                <S.StyledSelect
                  data-test-id='BoutiquesStyledSelect'
                  name='country'
                  onChange={(opt) => {
                    opt && handleSelectCountry(opt);
                  }}
                  options={countryOptions}
                  value={option}
                  variant='transparent'
                />
              </div>
            </S.TextContainer>
          </S.TextWrapper>
        </S.StyledContainer>
        <S.ImageWrapper data-test-id='BoutiquesImageWrapper'>
          <S.StyledImage
            alt='top banner'
            height={0}
            sizes='100vw'
            src={bannerImage}
            width={0}
            {...{
              priority: true,
              loading: 'eager',
            }}
          />
        </S.ImageWrapper>
        <S.Mask data-test-id='BoutiquesMask' />
      </S.TopBannerWrapper>
    </>
  );
};
